import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { MapPin, Users, Plus, Search, Building2, GraduationCap, Flag, UserPlus, LogOut as LeaveIcon } from "lucide-react";
import { toast } from "sonner";

const typeIcons = { club: Building2, academy: GraduationCap, national: Flag };
const typeColors = { club: "bg-[#074572]/5 text-[#074572]", academy: "bg-amber-50 text-amber-700", national: "bg-green-50 text-green-700" };

export default function TeamsPage() {
  const { user } = useAuth();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const myId = user?._id || user?.id;

  const fetchTeams = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (typeFilter) params.set("team_type", typeFilter);
      const { data } = await api.get(`/teams?${params}`);
      setTeams(data);
    } catch { toast.error("Failed to load teams"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchTeams(); }, [search, typeFilter]);

  const handleCreate = async (e) => {
    e.preventDefault();
    const form = new FormData(e.target);
    try {
      await api.post("/teams", {
        name: form.get("name"), description: form.get("description"),
        location: form.get("location"), team_type: form.get("team_type"),
      });
      toast.success("Team created!");
      setCreateOpen(false);
      fetchTeams();
    } catch { toast.error("Failed to create team"); }
  };

  const handleJoin = async (teamId) => {
    try {
      const { data } = await api.post(`/teams/${teamId}/join`);
      toast.success(`Joined ${data.team_name}!`);
      fetchTeams();
      if (selectedTeam?.id === teamId) {
        const { data: updated } = await api.get(`/teams/${teamId}`);
        setSelectedTeam(updated);
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleLeave = async (teamId) => {
    try {
      await api.post(`/teams/${teamId}/leave`);
      toast.success("Left team");
      fetchTeams();
      if (selectedTeam?.id === teamId) {
        const { data: updated } = await api.get(`/teams/${teamId}`);
        setSelectedTeam(updated);
      }
    } catch { toast.error("Failed"); }
  };

  const loadTeamDetail = async (teamId) => {
    try {
      const { data } = await api.get(`/teams/${teamId}`);
      setSelectedTeam(data);
    } catch { toast.error("Failed to load team"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="teams-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-medium text-zinc-900" style={{ fontFamily: 'Outfit' }}>Teams & Clubs</h1>
          <p className="text-sm text-zinc-500 mt-1">Explore and join football organizations</p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#F85037] hover:bg-[#e04530] text-white gap-2" data-testid="create-team-btn">
              <Plus size={16} /> Create Team
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle style={{ fontFamily: 'Outfit' }}>Create Team</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-2">
              <div><Label>Team Name</Label><Input name="name" required className="mt-1 border-[#E6E6E6]" data-testid="team-name-input" /></div>
              <div><Label>Description</Label><Textarea name="description" className="mt-1 border-[#E6E6E6]" data-testid="team-desc-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Location</Label><Input name="location" className="mt-1 border-[#E6E6E6]" data-testid="team-location-input" /></div>
                <div>
                  <Label>Type</Label>
                  <select name="team_type" className="mt-1 w-full border border-[#E6E6E6] rounded-md px-3 py-2 text-sm" data-testid="team-type-select">
                    <option value="club">Club</option>
                    <option value="academy">Academy</option>
                    <option value="national">National</option>
                  </select>
                </div>
              </div>
              <Button type="submit" className="w-full bg-[#074572] hover:bg-[#053a60] text-white" data-testid="team-submit-btn">Create Team</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-6">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input placeholder="Search teams..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 border-[#E6E6E6]" data-testid="team-search-input" />
        </div>
        <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-40 border-[#E6E6E6]" data-testid="team-type-filter">
            <SelectValue placeholder="All types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="club">Club</SelectItem>
            <SelectItem value="academy">Academy</SelectItem>
            <SelectItem value="national">National</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex gap-6">
        {/* Teams Grid */}
        <div className="flex-1">
          {loading ? (
            <div className="text-center py-16 text-zinc-400">Loading teams...</div>
          ) : teams.length === 0 ? (
            <div className="text-center py-16 text-zinc-400">
              <Building2 size={40} className="mx-auto mb-3 opacity-50" />
              <p className="font-medium">No teams found</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {teams.map(team => {
                const TypeIcon = typeIcons[team.team_type] || Building2;
                const isMember = team.members?.some(m => m.user_id === myId);
                const isOwner = team.owner_id === myId;
                return (
                  <div key={team.id} className="bg-white border border-[#E6E6E6] rounded-lg p-5 hover:shadow-md transition-shadow cursor-pointer" onClick={() => loadTeamDetail(team.id)} data-testid={`team-card-${team.id}`}>
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-12 h-12 rounded-lg bg-[#074572]/5 flex items-center justify-center shrink-0">
                        <TypeIcon size={20} className="text-[#074572]" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-semibold text-zinc-900 text-sm truncate" style={{ fontFamily: 'Outfit' }}>{team.name}</h3>
                        <Badge className={`text-[10px] mt-0.5 capitalize ${typeColors[team.team_type] || "bg-zinc-100 text-zinc-600"}`}>{team.team_type}</Badge>
                      </div>
                    </div>
                    {team.description && <p className="text-xs text-zinc-500 line-clamp-2 mb-3">{team.description}</p>}
                    <div className="flex items-center gap-3 text-xs text-zinc-400 mb-3">
                      {team.location && <span className="flex items-center gap-1"><MapPin size={11} />{team.location}</span>}
                      <span className="flex items-center gap-1"><Users size={11} />{team.members?.length || 0} members</span>
                    </div>
                    <Separator className="bg-[#E6E6E6] mb-3" />
                    {isMember ? (
                      <div className="flex gap-2">
                        <Badge className="bg-green-50 text-green-700 text-[10px]">Member</Badge>
                        {!isOwner && <button onClick={(e) => { e.stopPropagation(); handleLeave(team.id); }} className="text-xs text-zinc-400 hover:text-[#F85037]">Leave</button>}
                      </div>
                    ) : (
                      <Button size="sm" className="w-full bg-[#F85037] hover:bg-[#e04530] text-white text-xs gap-1" onClick={(e) => { e.stopPropagation(); handleJoin(team.id); }} data-testid={`join-team-${team.id}`}>
                        <UserPlus size={12} /> Join Team
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Team Detail Sidebar */}
        {selectedTeam && (
          <aside className="hidden lg:block w-80 shrink-0">
            <div className="bg-white border border-[#E6E6E6] rounded-lg p-6 sticky top-20" data-testid="team-detail">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-14 h-14 rounded-lg bg-[#074572]/5 flex items-center justify-center">
                  {(() => { const Icon = typeIcons[selectedTeam.team_type] || Building2; return <Icon size={24} className="text-[#074572]" />; })()}
                </div>
                <div>
                  <h2 className="font-semibold text-zinc-900" style={{ fontFamily: 'Outfit' }}>{selectedTeam.name}</h2>
                  <Badge className={`text-[10px] capitalize ${typeColors[selectedTeam.team_type]}`}>{selectedTeam.team_type}</Badge>
                </div>
              </div>
              {selectedTeam.description && <p className="text-sm text-zinc-500 mb-4">{selectedTeam.description}</p>}
              {selectedTeam.location && <p className="text-xs text-zinc-400 flex items-center gap-1 mb-4"><MapPin size={12} />{selectedTeam.location}</p>}
              <Separator className="bg-[#E6E6E6] mb-4" />
              <h3 className="text-xs font-semibold text-zinc-900 uppercase tracking-wide mb-3" style={{ fontFamily: 'Outfit' }}>Members ({selectedTeam.members?.length || 0})</h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {selectedTeam.members?.map(m => (
                  <Link key={m.user_id} to={`/profile/${m.user_id}`} className="flex items-center gap-2 p-2 rounded-md hover:bg-zinc-50 transition-colors">
                    <Avatar className="h-8 w-8 border border-[#E6E6E6]">
                      <AvatarFallback className="bg-[#074572] text-white text-[10px]">{m.name?.split(" ").map(n => n[0]).join("").toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-sm font-medium text-zinc-800">{m.name}</p>
                      <p className="text-[10px] text-zinc-400 capitalize">{m.role}{m.position ? ` - ${m.position}` : ""}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}
